import { useAsyncOp } from "@indietabletop/appkit/use-async-op";
import { useEffect } from "react";
import { config } from "../../config";
import { ArmyData } from "../../lib/db/types";

declare global {
  interface Window {
    /**
     * This value may be supplied by the server.
     *
     * See `netlify/functions/share.ts` for more detail.
     */
    PREFETCHED: { sharedArmy?: ArmyData } | undefined;
  }
}

type NetworkFailure = {
  type: "NETWORK_FAILURE";
};

type ApplicationFailure = {
  type: "UNEXPECTED_API_FAILURE";
};

type NotFoundFailure = {
  type: "ARMY_NOT_FOUND";
};

export type NetworkQueryOpFailure =
  | NetworkFailure
  | NotFoundFailure
  | ApplicationFailure;

export function useFetchSharedArmy(shareId: string) {
  const { op, setSuccess, setFailure } = useAsyncOp<
    ArmyData,
    NetworkQueryOpFailure
  >();

  useEffect(() => {
    async function getSharedArmy() {
      try {
        const prefetchedSharedArmy = window.PREFETCHED?.sharedArmy;

        if (prefetchedSharedArmy && prefetchedSharedArmy.share_id === shareId) {
          setSuccess(prefetchedSharedArmy);
          return;
        }

        const path = `${config.ITC_API_ORIGIN}/eternol/snapshots/${shareId}`;
        const response = await fetch(path);

        if (response.ok) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const data: ArmyData = await response.json();
          setSuccess(data);
          return;
        }

        if (response.status === 404) {
          setFailure({ type: "ARMY_NOT_FOUND" });
          return;
        }

        setFailure({ type: "UNEXPECTED_API_FAILURE" });
      } catch (error) {
        console.error(error);
        setFailure({ type: "NETWORK_FAILURE" });
      }
    }

    void getSharedArmy();
  }, [shareId, setSuccess, setFailure]);

  return op;
}
